/**
 * (#27) Plantilla de importación de ejercicios.
 *
 * Lo que se descargaba antes era una fila con `name`, `description` y `videoUrl`
 * y el resto de las columnas en blanco, con la ayuda diciendo "ID de la
 * categoría". Un coach no tiene forma de saber esos IDs, así que la plantilla no
 * servía de ejemplo: era una lista de encabezados.
 *
 * Dos cosas cambian eso:
 *
 *  - El backend resuelve los catálogos por **nombre además de por id**
 *    (`resolveCatalogId` en `exercise.service.ts`), así que la plantilla puede
 *    traer "CUADRICEPS" en lugar de un número.
 *  - Los nombres válidos salen de la base del coach que descarga, no de una
 *    lista escrita a mano acá: un nombre que no está en el catálogo se descarta
 *    **en silencio** al importar, y el coach nunca se entera de por qué su
 *    ejercicio quedó sin músculos.
 */

export interface ICatalogItem {
  id: number;
  name: string;
}

export interface ITemplateCatalogs {
  categories: ICatalogItem[];
  variants: ICatalogItem[];
  contractions: ICatalogItem[];
  difficulties: ICatalogItem[];
  movementPlanes: ICatalogItem[];
  unilateralTypes: ICatalogItem[];
  movementPatterns: ICatalogItem[];
  equipments: ICatalogItem[];
  muscles: ICatalogItem[];
  rpeMethods: ICatalogItem[];
}

export type TemplateRow = Record<string, string>;

export interface ITemplateColumn {
  key: string;
  /** Clave i18n del encabezado que sale en el Excel. */
  headerId: string;
  descriptionId: string;
  required: boolean;
  /**
   * Nombres normalizados que el auto-mapeo reconoce. Incluyen los encabezados de
   * la plantilla en español y en inglés: sin eso, un archivo bajado de acá
   * dependería del match por substring y del orden de las columnas.
   */
  aliases: string[];
}

export interface ICatalogColumnMeta {
  catalog: keyof ITemplateCatalogs;
  /** True cuando la celda admite varios valores separados por coma. */
  multi: boolean;
}

/**
 * Columnas del archivo, en el orden en que salen en el Excel.
 *
 * `description` NO está: el import con mapeo (`processImportExercises`) arma el
 * Exercise sin ese campo, así que ofrecerla en la plantilla promete algo que el
 * sistema descarta sin avisar.
 */
export const TEMPLATE_COLUMNS: ITemplateColumn[] = [
  {
    key: 'name',
    headerId: 'coach.importExercises.help.header.name',
    descriptionId: 'coach.importExercises.help.col.name',
    required: true,
    aliases: ['name', 'nombre', 'ejercicio', 'exercise', 'nombredelejercicio', 'exercisename']
  },
  {
    key: 'videoUrl',
    headerId: 'coach.importExercises.help.header.videoUrl',
    descriptionId: 'coach.importExercises.help.col.videoUrl',
    required: false,
    aliases: ['multimedia', 'videourl', 'video', 'url', 'link', 'youtube', 'videoyoutube']
  },
  {
    key: 'category_id',
    headerId: 'coach.importExercises.help.header.category_id',
    descriptionId: 'coach.importExercises.help.col.category_id',
    required: false,
    aliases: ['categoryid', 'categoria', 'category', 'tipodeejercicio', 'tipoejercicio']
  },
  {
    key: 'variant_id',
    headerId: 'coach.importExercises.help.header.variant_id',
    descriptionId: 'coach.importExercises.help.col.variant_id',
    required: false,
    aliases: ['variantid', 'variante', 'variant']
  },
  {
    key: 'contraction_id',
    headerId: 'coach.importExercises.help.header.contraction_id',
    descriptionId: 'coach.importExercises.help.col.contraction_id',
    required: false,
    aliases: ['contractionid', 'contraccion', 'contraction', 'tipodecontraccion', 'contractiontype']
  },
  {
    key: 'difficulty_id',
    headerId: 'coach.importExercises.help.header.difficulty_id',
    descriptionId: 'coach.importExercises.help.col.difficulty_id',
    required: false,
    aliases: ['difficultyid', 'dificultad', 'difficulty', 'nivel']
  },
  {
    key: 'movement_plane_id',
    headerId: 'coach.importExercises.help.header.movement_plane_id',
    descriptionId: 'coach.importExercises.help.col.movement_plane_id',
    required: false,
    aliases: ['movementplaneid', 'plano', 'plane', 'planodemovimiento', 'movementplane']
  },
  {
    key: 'measurement_method_id',
    headerId: 'coach.importExercises.help.header.measurement_method_id',
    descriptionId: 'coach.importExercises.help.col.measurement_method_id',
    required: false,
    aliases: [
      'measurementmethodid',
      'rpe',
      'rir',
      'medicion',
      'method',
      'metododemedicionrpeorir',
      'measurementmethodrpeorrir'
    ]
  },
  {
    key: 'unilateral_id',
    headerId: 'coach.importExercises.help.header.unilateral_id',
    descriptionId: 'coach.importExercises.help.col.unilateral_id',
    required: false,
    aliases: ['unilateralid', 'unilateral', 'bilateral', 'unilateralobilateral', 'unilateralorbilateral']
  },
  {
    key: 'movement_pattern_id',
    headerId: 'coach.importExercises.help.header.movement_pattern_id',
    descriptionId: 'coach.importExercises.help.col.movement_pattern_id',
    required: false,
    aliases: ['movementpatternid', 'patron', 'pattern', 'patrondemovimiento', 'movementpattern']
  },
  {
    key: 'equipment_ids',
    headerId: 'coach.importExercises.help.header.equipment_ids',
    descriptionId: 'coach.importExercises.help.col.equipment_ids',
    required: false,
    aliases: [
      'equipmentids',
      'equipamiento',
      'equipamento',
      'equipment',
      'equipo',
      'equipamientonecesario',
      'requiredequipment'
    ]
  },
  {
    key: 'muscle_ids',
    headerId: 'coach.importExercises.help.header.muscle_ids',
    descriptionId: 'coach.importExercises.help.col.muscle_ids',
    required: false,
    aliases: ['muscleids', 'musculos', 'muscle', 'musculo', 'musculosinvolucrados', 'musclesinvolved']
  },
  {
    key: 'regression_exercise_id',
    headerId: 'coach.importExercises.help.header.regression_exercise_id',
    descriptionId: 'coach.importExercises.help.col.regression_exercise_id',
    required: false,
    aliases: ['regressionexerciseid', 'regresion', 'regression', 'ejerciciodregresion', 'regressionexercise']
  },
  {
    key: 'progression_exercise_id',
    headerId: 'coach.importExercises.help.header.progression_exercise_id',
    descriptionId: 'coach.importExercises.help.col.progression_exercise_id',
    required: false,
    aliases: ['progressionexerciseid', 'progresion', 'progression', 'ejerciciodeprogresion', 'progressionexercise']
  }
];

/** Qué columnas se resuelven contra un catálogo, y cuáles admiten varios valores. */
export const CATALOG_BY_COLUMN: Record<string, ICatalogColumnMeta> = {
  category_id: { catalog: 'categories', multi: false },
  variant_id: { catalog: 'variants', multi: false },
  contraction_id: { catalog: 'contractions', multi: false },
  difficulty_id: { catalog: 'difficulties', multi: false },
  movement_plane_id: { catalog: 'movementPlanes', multi: false },
  measurement_method_id: { catalog: 'rpeMethods', multi: false },
  unilateral_id: { catalog: 'unilateralTypes', multi: false },
  movement_pattern_id: { catalog: 'movementPatterns', multi: false },
  equipment_ids: { catalog: 'equipments', multi: true },
  muscle_ids: { catalog: 'muscles', multi: true }
};

/**
 * Ejemplos escritos contra el seed de la plataforma. Son una PREFERENCIA, no una
 * garantía: si el catálogo del coach no tiene alguno de estos nombres,
 * `buildExampleRows` lo reemplaza por uno que sí exista.
 */
const EXAMPLES: TemplateRow[] = [
  {
    name: 'Sentadilla con barra',
    videoUrl: 'https://www.youtube.com/watch?v=ultWZbUMPL8',
    category_id: 'CONTROL',
    variant_id: '',
    contraction_id: 'DINAMICA',
    difficulty_id: 'Intermedio',
    movement_plane_id: 'SAGITAL',
    measurement_method_id: 'RPE 1-10',
    unilateral_id: 'BILATERAL',
    movement_pattern_id: 'RODILLA',
    equipment_ids: 'BARRA_OLIMPICA, DISCO, RACK_DE_SENTADILLAS',
    muscle_ids: 'CUADRICEPS, ISQUIOTIBIALES',
    regression_exercise_id: '',
    progression_exercise_id: ''
  },
  {
    name: 'Dominadas pronadas',
    videoUrl: 'https://www.youtube.com/watch?v=eGo4IYlbE5g',
    category_id: 'CONTROL',
    variant_id: 'CALISTENIA_FULL',
    contraction_id: 'DINAMICA',
    difficulty_id: 'Avanzado',
    movement_plane_id: 'FRONTAL',
    measurement_method_id: 'RIR 0-5',
    unilateral_id: 'BILATERAL',
    movement_pattern_id: 'TRACCIÓN',
    equipment_ids: 'BARRA_DE_DOMINADAS',
    muscle_ids: 'DORSAL_ANCHO, BICEPS_BRAQUIAL',
    regression_exercise_id: '',
    progression_exercise_id: ''
  },
  {
    name: 'Peso muerto rumano con mancuernas',
    videoUrl: '',
    category_id: 'CONTROL',
    variant_id: '',
    contraction_id: 'EXCENTRICA',
    difficulty_id: 'Intermedio',
    movement_plane_id: 'SAGITAL',
    measurement_method_id: 'RPE 1-10',
    unilateral_id: 'BILATERAL',
    movement_pattern_id: 'CADERA',
    equipment_ids: 'MANCUERNA',
    muscle_ids: 'ISQUIOTIBIALES, ERECTORES_DE_LA_ESPINA',
    regression_exercise_id: '',
    progression_exercise_id: ''
  },
  {
    name: 'Zancada búlgara',
    videoUrl: '',
    category_id: 'CONTROL',
    variant_id: '',
    contraction_id: 'DINAMICA',
    difficulty_id: 'Intermedio',
    movement_plane_id: 'SAGITAL',
    measurement_method_id: 'RPE 1-10',
    unilateral_id: 'UNILATERAL',
    movement_pattern_id: 'RODILLA',
    equipment_ids: 'MANCUERNA, BANCO_PLANO',
    muscle_ids: 'CUADRICEPS',
    regression_exercise_id: '',
    progression_exercise_id: ''
  },
  {
    name: 'Plancha isométrica',
    videoUrl: '',
    category_id: 'CONTROL',
    variant_id: '',
    contraction_id: 'ISOMETRICA',
    difficulty_id: 'Principiante',
    movement_plane_id: 'SAGITAL',
    measurement_method_id: '',
    unilateral_id: 'BILATERAL',
    movement_pattern_id: 'CORE',
    equipment_ids: '',
    muscle_ids: 'RECTO_ANTERIOR_DEL_ABDOMEN, OBLICUOS_ABDOMINALES',
    regression_exercise_id: '',
    progression_exercise_id: ''
  },
  {
    name: 'Salto al cajón',
    videoUrl: '',
    category_id: 'BALISTICO',
    variant_id: 'BALISTICO_SALTO',
    contraction_id: 'DINAMICA',
    difficulty_id: 'Intermedio',
    movement_plane_id: 'SAGITAL',
    measurement_method_id: '',
    unilateral_id: 'BILATERAL',
    movement_pattern_id: 'RODILLA',
    equipment_ids: 'CAJON',
    muscle_ids: 'CUADRICEPS, GASTROCNEMIOS',
    regression_exercise_id: '',
    progression_exercise_id: ''
  }
];

const normalize = (value: string): string => value.trim().toLowerCase();

/**
 * Ajusta un valor de ejemplo al catálogo real.
 *
 * Si el nombre preferido está, se usa tal cual. Si no está pero el catálogo tiene
 * otros valores, se cae al primero: la fila sigue siendo importable y le muestra
 * al coach cómo se escribe. Si el catálogo llegó vacío se devuelve vacío, porque
 * inventar un nombre generaría una celda que el import descarta sin avisar.
 */
function fitToCatalog(preferred: string, list: ICatalogItem[] | undefined, multi: boolean): string {
  if (!list || list.length === 0) return '';

  const names = list.map((item) => item.name);
  const tokens = (multi ? preferred.split(',') : [preferred]).map((t) => t.trim()).filter((t) => t.length > 0);

  const resolved = tokens
    .map((token) => names.find((name) => normalize(name) === normalize(token)))
    .filter((name): name is string => Boolean(name));

  if (resolved.length > 0) return resolved.join(', ');

  const primero = names[0];
  return preferred.trim() && primero ? primero : '';
}

/**
 * Filas de ejemplo listas para pegar en el Excel.
 *
 * @param catalogs catálogos del coach. Sin ellos se devuelven los nombres del
 * seed, que sirven de referencia pero pueden no coincidir con su base.
 */
export function buildExampleRows(catalogs?: Partial<ITemplateCatalogs>): TemplateRow[] {
  return EXAMPLES.map((example) => {
    const row: TemplateRow = {};
    TEMPLATE_COLUMNS.forEach(({ key }) => {
      const meta = CATALOG_BY_COLUMN[key];
      if (!meta || !catalogs) {
        row[key] = example[key] ?? '';
        return;
      }
      row[key] = fitToCatalog(example[key] ?? '', catalogs[meta.catalog], meta.multi);
    });
    return row;
  });
}

export interface ICatalogBlock {
  /** Columna de la hoja de ejercicios que consume estos valores. */
  column: string;
  multi: boolean;
  items: ICatalogItem[];
}

/**
 * Hoja de referencia: una tabla por catálogo con `id` y `name`.
 *
 * Se arma por bloques y no como una lista larga de (columna, valor) para que cada
 * catálogo sea un rango contiguo de dos columnas: así se le puede apuntar un
 * BUSCARV desde la hoja de ejercicios. Los catálogos que no llegaron se omiten,
 * en lugar de dejar una tabla vacía que parece un catálogo sin valores.
 */
export function buildCatalogBlocks(catalogs: Partial<ITemplateCatalogs>): ICatalogBlock[] {
  return Object.entries(CATALOG_BY_COLUMN)
    .map(([column, meta]) => ({
      column,
      multi: meta.multi,
      items: catalogs[meta.catalog] ?? []
    }))
    .filter((block) => block.items.length > 0);
}

export interface ICatalogSheetLabels {
  id: string;
  name: string;
  /** Encabezado legible por clave interna; si falta se cae a la clave. */
  headerByKey?: Record<string, string>;
  /** Aclaración para las columnas que aceptan varios valores. */
  multiHint?: string;
}

export interface ICatalogSheetBlock {
  /** Columna del Excel donde arranca la tabla, 0-based. */
  startColumn: number;
  rows: Array<Array<string | number>>;
}

/** Ancho de cada tabla (id + nombre) más una columna de aire entre tablas. */
const BLOCK_WIDTH = 3;

/**
 * Ubica las tablas una al lado de la otra y arma sus celdas.
 *
 * Lado a lado y no una debajo de otra porque los catálogos tienen alturas muy
 * distintas (2 métodos de medición contra 39 equipamientos): apiladas, encontrar
 * dónde empieza cada una obliga a scrollear y los rangos del BUSCARV se mueven
 * cada vez que se agrega un valor al seed.
 */
export function layoutCatalogSheet(blocks: ICatalogBlock[], labels: ICatalogSheetLabels): ICatalogSheetBlock[] {
  const hint = labels.multiHint ?? 'varios, separados por coma';
  return blocks.map((block, index) => ({
    startColumn: index * BLOCK_WIDTH,
    rows: [
      [labels.headerByKey?.[block.column] ?? block.column, block.multi ? hint : ''],
      [labels.id, labels.name],
      ...block.items.map((item): Array<string | number> => [item.id, item.name])
    ]
  }));
}

/** Normaliza un encabezado para compararlo: sin acentos, sin espacios ni símbolos. */
export function normalizeHeader(value: string): string {
  return (value || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '');
}

/**
 * Asocia cada columna del archivo del coach con un campo del modelo.
 *
 * Dos pasadas: primero coincidencia exacta contra los alias, después por
 * substring con alias de 4 caracteres o más. Un campo se usa una sola vez.
 *
 * Vive acá y no en el diálogo porque los alias y los encabezados de la plantilla
 * tienen que moverse juntos: si el encabezado cambia y el alias no, el archivo
 * que genera la propia app deja de auto-mapearse y el coach tiene que asociar
 * las catorce columnas a mano.
 */
export function autoAssign(columns: string[]): Record<string, string> {
  const result: Record<string, string> = {};
  const usedFields = new Set<string>();
  const normCols = columns.map((c) => ({ raw: c, norm: normalizeHeader(c) }));

  normCols.forEach((c) => {
    const field = TEMPLATE_COLUMNS.find(
      (f) => !usedFields.has(f.key) && [...f.aliases, normalizeHeader(f.key)].includes(c.norm)
    );
    if (field) {
      result[c.raw] = field.key;
      usedFields.add(field.key);
    }
  });

  normCols.forEach((c) => {
    if (result[c.raw]) return;
    const field = TEMPLATE_COLUMNS.find(
      (f) =>
        !usedFields.has(f.key) &&
        [...f.aliases, normalizeHeader(f.key)].some((a) => a.length >= 4 && (c.norm.includes(a) || a.includes(c.norm)))
    );
    if (field) {
      result[c.raw] = field.key;
      usedFields.add(field.key);
    }
  });

  columns.forEach((c) => {
    if (result[c] === undefined) result[c] = '';
  });
  return result;
}

/**
 * Pasa las filas de claves internas (`muscle_ids`) a los encabezados que ve el
 * coach ("Músculos involucrados"), respetando el orden de la plantilla.
 */
export function toSheetRows(rows: TemplateRow[], headerByKey: Record<string, string>): TemplateRow[] {
  return rows.map((row) => {
    const salida: TemplateRow = {};
    TEMPLATE_COLUMNS.forEach(({ key }) => {
      const header = headerByKey[key] ?? key;
      salida[header] = row[key] ?? '';
    });
    return salida;
  });
}
